import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Icon,
  type IconName,
  type SpacingToken,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
  useKeyboardHints,
} from "@salestrak/ui";
import { useEditor, EditorContent, type Editor, type Range } from "@tiptap/react";
import { DragHandle } from "@tiptap/extension-drag-handle-react";
import { offset } from "@floating-ui/dom";
import StarterKit from "@tiptap/starter-kit";
import Image from "@tiptap/extension-image";
import { Table } from "@tiptap/extension-table";
import { TableRow } from "@tiptap/extension-table-row";
import { TableCell } from "@tiptap/extension-table-cell";
import { TableHeader } from "@tiptap/extension-table-header";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Placeholder from "@tiptap/extension-placeholder";
import { TextStyle } from "@tiptap/extension-text-style";
import Color from "@tiptap/extension-color";
import TextAlign from "@tiptap/extension-text-align";
import { type Extensions } from "@tiptap/core";
import { Fragment, Slice, type Node as ProseMirrorNode } from "@tiptap/pm/model";
import type { Transaction } from "@tiptap/pm/state";
import { dropPoint } from "@tiptap/pm/transform";
import { EditorToolbar } from "./editor-toolbar";
import { Indent } from "./indent-extension";
import { PasteHygiene } from "./paste-hygiene";
import { PhoneLink } from "./phone-link";
import { EntityMention, buildEntitySuggestion } from "./entity-mention";
import { getEnabledMentionSources } from "./mention-sources";
import { useMentionEntityResolver } from "./use-mention-entity-resolver";
import { SlashCommand, filterSlashItems, type SlashItem } from "./slash-command";
import "./editor-styles.css";
import type { Omittable } from "@salestrak/ui";

function hasHeadingLevel(editor: Editor, level: number): boolean {
  const heading = editor.extensionManager.extensions.find(
    (ext) => ext.name === "heading",
  );
  const levels =
    (heading?.options as { levels?: number[] } | undefined)?.levels ?? [];
  return levels.includes(level);
}

type SlashItemDef = SlashItem & Omittable<{
  // Capability gate: only offer the item when the command is registered —
  // invoking an unregistered command throws (it's undefined on the chain).
  available?: (editor: Editor) => boolean;
}>;

const insertTrigger =
  (char: string) => (editor: Editor, range: Range) =>
    editor.chain().focus().deleteRange(range).insertContent(char).run();
const block =
  (fn: (e: Editor) => void) => (editor: Editor, range: Range) => {
    editor.chain().focus().deleteRange(range).run();
    fn(editor);
  };

const slashTile = (name: IconName) => () => (
  <Box
    width={26}
    height={26}
    border
    rounded="md"
    direction="column"
    align="center"
    justify="center"
    shrink={false}
  >
    <Icon name={name} size="xs" color="muted" />
  </Box>
);

const SLASH_ITEMS: SlashItemDef[] = [
  {
    id: "text-normal",
    title: "Normal text",
    section: "Text",
    renderAvatar: slashTile("type"),
    run: block((e) => e.chain().focus().setParagraph().run()),
  },
  {
    id: "text-h1",
    title: "Heading 1",
    section: "Text",
    renderAvatar: slashTile("heading-1"),
    available: (e) => hasHeadingLevel(e, 1),
    run: block((e) => e.chain().focus().toggleHeading({ level: 1 }).run()),
  },
  {
    id: "text-h2",
    title: "Heading 2",
    section: "Text",
    renderAvatar: slashTile("heading-2"),
    available: (e) => hasHeadingLevel(e, 2),
    run: block((e) => e.chain().focus().toggleHeading({ level: 2 }).run()),
  },
  {
    id: "text-h3",
    title: "Heading 3",
    section: "Text",
    renderAvatar: slashTile("heading-3"),
    available: (e) => hasHeadingLevel(e, 3),
    run: block((e) => e.chain().focus().toggleHeading({ level: 3 }).run()),
  },
  {
    id: "text-h4",
    title: "Heading 4",
    section: "Text",
    renderAvatar: slashTile("heading-4"),
    available: (e) => hasHeadingLevel(e, 4),
    run: block((e) => e.chain().focus().toggleHeading({ level: 4 }).run()),
  },
  {
    id: "list-bullet",
    title: "Bulleted list",
    section: "Lists",
    renderAvatar: slashTile("list"),
    run: block((e) => e.chain().focus().toggleBulletList().run()),
  },
  {
    id: "list-ordered",
    title: "Numbered list",
    section: "Lists",
    renderAvatar: slashTile("list-ordered"),
    run: block((e) => e.chain().focus().toggleOrderedList().run()),
  },
  {
    id: "list-task",
    title: "Checklist",
    section: "Lists",
    renderAvatar: slashTile("list-checks"),
    available: (e) => "toggleTaskList" in e.commands,
    run: block((e) => e.chain().focus().toggleTaskList().run()),
  },
  {
    id: "block-quote",
    title: "Quote",
    section: "Blocks",
    renderAvatar: slashTile("quote"),
    run: block((e) => e.chain().focus().toggleBlockquote().run()),
  },
  {
    id: "block-code",
    title: "Code block",
    section: "Blocks",
    renderAvatar: slashTile("square-code"),
    run: block((e) => e.chain().focus().toggleCodeBlock().run()),
  },
  {
    id: "block-divider",
    title: "Divider",
    section: "Blocks",
    renderAvatar: slashTile("minus"),
    run: block((e) => e.chain().focus().setHorizontalRule().run()),
  },
  {
    id: "block-table",
    title: "Table",
    section: "Blocks",
    renderAvatar: slashTile("table"),
    available: (e) => "insertTable" in e.commands,
    run: block((e) =>
      e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run(),
    ),
  },
];

function buildMentionSlashItems(): SlashItemDef[] {
  return getEnabledMentionSources().map((source) => ({
    id: `mention-${source.kind}`,
    title: source.slash.title,
    section: "Mention",
    renderAvatar: slashTile(source.slash.icon),
    run: insertTrigger(source.char),
  }));
}

// Builds the "/photo" item against the host's picker (file prompt + upload).
// The pick/upload is async, so `run` kicks the flow off and inserts the image
// node when the object URL arrives.
function buildPhotoSlashItem(
  pickPhoto: () => Promise<{ objectUrl: string } | null>,
): SlashItemDef {
  return {
    id: "block-photo",
    title: "Photo",
    section: "Blocks",
    renderAvatar: slashTile("image"),
    available: (e) => "setImage" in e.commands,
    run: (editor, range) => {
      editor.chain().focus().deleteRange(range).run();
      void pickPhoto().then((result) => {
        if (!result || editor.isDestroyed) return;
        editor.chain().focus().setImage({ src: result.objectUrl }).run();
      });
    },
  };
}

function getSlashItems(
  query: string,
  editor: Editor,
  mentions: boolean,
  pickPhoto?: () => Promise<{ objectUrl: string } | null>,
): SlashItem[] {
  // A mention item inserts the trigger char, which only opens a picker when
  // the mention extension is registered — without it, "@" is a dead keystroke.
  const items = [
    ...SLASH_ITEMS,
    ...(pickPhoto ? [buildPhotoSlashItem(pickPhoto)] : []),
    ...(mentions ? buildMentionSlashItems() : []),
  ];
  return filterSlashItems(
    items.filter((item) => item.available?.(editor) ?? true),
    query,
  );
}

// Appended to the consumer's placeholder prefix when `shortcutHints` is on.
const SHORTCUT_HINT = " or type '/' for commands...";

/**
 * Compose a placeholder with the shortcut-hint suffix — the exact string the
 * editor shows when `shortcutHints` is on. Lets lazily-mounted composers (which
 * render a plain placeholder before TipTap mounts) match the editor's wording
 * from one source of truth.
 */
export function withShortcutHints(placeholder: string): string {
  return `${placeholder}${SHORTCUT_HINT}`;
}

type RichTextEditorProps = Omittable<{
  content: string;
  onUpdate: (html: string) => void;
  /** The editor's accessible name ("Comment", a field's name). The editable
   *  surface is a multi-line textbox with this name; a placeholder is only a
   *  hint and names nothing. */
  label: string;
  /** Placeholder prefix shown in an empty editor. Pair with `shortcutHints`. */
  placeholder?: string;
  /** Append " or type '/' for commands..." to `placeholder`. */
  shortcutHints?: boolean;
  editable?: boolean;
  /** Register the @/# entity mention pickers (and their slash items). */
  mentions?: boolean;
  /** Register the "/" command menu (headings, lists, blocks…). Defaults to
   *  `mentions`, so every mention-enabled composer keeps its slash menu; a
   *  host with no mentions turns it on by itself. */
  slashCommands?: boolean;
  /** Compact mode: fewer extensions and a slimmer toolbar for note/comment editing */
  compact?: boolean;
  /** Auto-focus the editor when it mounts */
  autoFocus?: boolean;
  /** Padding around the editable content only. The desktop toolbar floats and
   *  the mobile one docks to the keyboard, so neither sits inside this inset. */
  contentPaddingX?: SpacingToken;
  contentPaddingY?: SpacingToken;
  /**
   * Host-provided photo picker (file prompt + upload). When set, a "Photo"
   * slash item is offered; the resolved object URL is inserted as an inline
   * image node. The host owns persistence (rewriting srcs, linking ids).
   */
  onPickPhoto?: () => Promise<{ objectUrl: string } | null>;
  /**
   * Host-provided upload for files dropped on the editor. Resolving with an
   * object URL inserts it as an inline image at the drop point; resolving
   * null means the host took the file some other way (or rejected it).
   */
  onDropFile?: (file: File) => Promise<{ objectUrl: string } | null>;
  /**
   * A grip beside the hovered block that drags it to a new place in the
   * document. The grip stands in the gutter to the block's left — the
   * 16px glyph column, 8px off the text — and shows only under a pointer
   * that hovers, so a touch surface never sees it. Editable hosts only.
   */
  blockHandles?: boolean;
  /** The editor is as tall as its content and no taller: an empty one is its
   *  placeholder line. A field in a list of fields; a composer reserves a
   *  writing area instead. */
  fitContent?: boolean;
  /** Inline images render as thumbnail tiles (notes). */
  mediaThumbnails?: boolean;
  /** Mount the editor's own formatting bar (the desktop bubble menu, the
   *  phone keyboard dock). A host that seats the tools itself turns it off
   *  and reaches the editor through `onEditor`. */
  toolbar?: boolean;
  /** Receives the live editor once it mounts (null as it goes). */
  onEditor?: (editor: Editor | null) => void;
}>;

const DROPPED_FILE = "droppedFile";

type DroppedFileMeta = { spots: number[]; index: number };

export function RichTextEditor({
  content,
  onUpdate,
  label,
  placeholder = "Write",
  shortcutHints = false,
  editable = true,
  mentions = false,
  slashCommands = mentions,
  compact = false,
  autoFocus = false,
  contentPaddingX,
  contentPaddingY,
  onPickPhoto,
  onDropFile,
  blockHandles = false,
  fitContent = false,
  mediaThumbnails = false,
  toolbar: showToolbar = true,
  onEditor,
}: RichTextEditorProps) {
  // The block under the pointer, as the drag handle reports it: an empty
  // paragraph gets a "+" that opens the "/" menu instead of a grip.
  const [handleBlock, setHandleBlock] = useState<{ node: ProseMirrorNode | null; pos: number }>({
    node: null,
    pos: -1,
  });
  // Extensions are built once, so the resolver flows through a ref.
  const resolveEntity = useMentionEntityResolver();
  const resolveEntityRef = useRef(resolveEntity);
  resolveEntityRef.current = resolveEntity;
  const onPickPhotoRef = useRef(onPickPhoto);
  onPickPhotoRef.current = onPickPhoto;
  const onDropFileRef = useRef(onDropFile);
  onDropFileRef.current = onDropFile;

  // The "/" suffix is a keyboard tip: on a touch screen it stands down with
  // every other keycap until a hardware keyboard is heard. The placeholder
  // reads through a ref so that flip (or a new prompt) re-renders the
  // decoration without rebuilding the extensions — which would recreate the
  // editor and drop what was typed.
  const keyboardHints = useKeyboardHints();
  const hintsOn = shortcutHints && keyboardHints;
  const placeholderRef = useRef({ prompt: placeholder, hints: hintsOn });
  placeholderRef.current = { prompt: placeholder, hints: hintsOn };

  const extensions = useMemo<Extensions>(() => {
    const linkOptions = {
      openOnClick: false,
      HTMLAttributes: { target: "_blank", rel: "noopener noreferrer" },
    };
    // An empty document wears the host's prompt on its first line. Any other
    // empty paragraph the caret sits on wears the writing prompt — the line
    // says what it takes, the way a new block in a document does — but only
    // where the "/" menu exists to be named.
    const placeholderFor = ({ node, editor: ed }: { node: ProseMirrorNode; editor: Editor }) => {
      if (node.type.name !== "paragraph") return "";
      const { prompt, hints } = placeholderRef.current;
      if (ed.isEmpty) return hints ? withShortcutHints(prompt) : prompt;
      return slashCommands && hints ? withShortcutHints("Write") : "";
    };

    const base: Extensions = compact
      ? [
          StarterKit.configure({
            heading: { levels: [1, 2, 3] },
            link: linkOptions,
          }),
          // Notes embed photos via the "/photo" slash item, so the compact
          // editor needs the image node too.
          Image.configure({ inline: false, allowBase64: true }),
          // Checklists: the "/checklist" slash item and the toolbar's list
          // menu both gate on the command, so registering the node is what
          // offers them.
          TaskList,
          TaskItem.configure({ nested: true }),
          PhoneLink,
          PasteHygiene,
          Placeholder.configure({ placeholder: placeholderFor }),
        ]
      : [
          StarterKit.configure({
            heading: { levels: [1, 2, 3, 4] },
            link: linkOptions,
          }),
          TextStyle,
          Color,
          TextAlign.configure({
            types: ["heading", "paragraph"],
            alignments: ["left", "center", "right"],
          }),
          Indent,
          Image.configure({ inline: false, allowBase64: true }),
          Table.configure({ resizable: true }),
          TableRow,
          TableCell,
          TableHeader,
          TaskList,
          TaskItem.configure({ nested: true }),
          PhoneLink,
          PasteHygiene,
          Placeholder.configure({ placeholder: placeholderFor }),
        ];

    if (mentions) {
      base.push(
        EntityMention.configure({
          HTMLAttributes: { class: "mention" },
          resolveEntity: (ref) => resolveEntityRef.current(ref),
          suggestions: getEnabledMentionSources().map(buildEntitySuggestion),
        }),
      );
    }

    if (slashCommands) {
      base.push(
        SlashCommand.configure({
          getItems: (query: string, editor: Editor) =>
            getSlashItems(query, editor, mentions, onPickPhotoRef.current),
        }),
      );
    }

    return base;
  }, [compact, mentions, slashCommands]);

  const editor = useEditor({
    extensions,
    content,
    editable,
    onUpdate: ({ editor: ed }) => {
      onUpdate(ed.getHTML());
    },
    editorProps: {
      attributes: {
        class: "focus:outline-none",
        role: "textbox",
        "aria-multiline": "true",
        "aria-label": label,
      },
      handleDrop(view, event, _slice, moved) {
        const dropFile = onDropFileRef.current;
        const host = editorRef.current;
        const imageType = view.state.schema.nodes.image;
        const files = Array.from(event.dataTransfer?.files ?? []);
        if (moved || !dropFile || !host || !imageType || !files.length) return false;
        event.preventDefault();
        const probe = new Slice(Fragment.from(imageType.create()), 0, 0);
        const at = view.posAtCoords({ left: event.clientX, top: event.clientY });
        const start = at?.pos ?? view.state.selection.from;
        // Each file's spot rides every edit made while the uploads run. A
        // sibling's insert keeps earlier files before it and later ones after
        // it, so the images keep drop order whatever order the uploads finish.
        const spots = files.map(() => dropPoint(view.state.doc, start, probe) ?? start);
        const follow = ({ transaction }: { transaction: Transaction }) => {
          const sibling = transaction.getMeta(DROPPED_FILE) as DroppedFileMeta | undefined;
          const index = sibling?.spots === spots ? sibling.index : -1;
          spots.forEach((spot, i) => {
            spots[i] = transaction.mapping.map(spot, i < index ? -1 : 1);
          });
        };
        host.on("transaction", follow);
        let open = files.length;
        files.forEach((file, index) => {
          void dropFile(file)
            .then((result) => {
              if (!result || view.isDestroyed) return;
              const { tr } = view.state;
              const image = imageType.create({ src: result.objectUrl });
              const pos = Math.min(spots[index] ?? start, tr.doc.content.size);
              const point = dropPoint(tr.doc, pos, probe);
              view.dispatch(
                (point == null ? tr.replaceSelectionWith(image) : tr.insert(point, image)).setMeta(
                  DROPPED_FILE,
                  { spots, index } satisfies DroppedFileMeta,
                ),
              );
            })
            .finally(() => {
              open -= 1;
              if (!open) host.off("transaction", follow);
            });
        });
        return true;
      },
    },
  });
  const editorRef = useRef(editor);
  editorRef.current = editor;

  // The tiptap-* classes scope editor-styles.css to this subtree — a CSS
  // hook for a React-unmanaged DOM tree, not chrome (tracked className).
  const cssClass = [
    "tiptap-editor",
    compact && "tiptap-compact",
    fitContent && "tiptap-fit",
    mediaThumbnails && "tiptap-media-thumbs",
  ]
    .filter(Boolean)
    .join(" ");

  useEffect(() => {
    if (editor) editor.setEditable(editable);
  }, [editor, editable]);

  const onEditorRef = useRef(onEditor);
  onEditorRef.current = onEditor;
  useEffect(() => {
    onEditorRef.current?.(editor);
    return () => onEditorRef.current?.(null);
  }, [editor]);

  // Placeholder decorations recompute on a transaction; a changed prompt or
  // hint rung dispatches a no-op one so the empty line repaints.
  useEffect(() => {
    if (!editor || editor.isDestroyed) return;
    editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [editor, placeholder, hintsOn]);

  // NodeView chips don't re-render when the workspace maps (and thus the
  // resolver) hydrate, so when the resolver identity changes dispatch a no-op,
  // non-undoable transaction to re-run node views with the resolvable data.
  const lastResolverRef = useRef(resolveEntity);
  useEffect(() => {
    if (!editor) return;
    if (lastResolverRef.current === resolveEntity) return;
    lastResolverRef.current = resolveEntity;
    editor.view.dispatch(editor.state.tr.setMeta("addToHistory", false));
  }, [editor, resolveEntity]);

  useEffect(() => {
    if (!autoFocus || !editor) return;
    // Guard against a destroyed editor: @tiptap/react destroys via a deferred
    // setTimeout that can beat a pending (throttled) rAF, nulling commandManager.
    const frame = requestAnimationFrame(() => {
      if (editor.isDestroyed) return;
      editor.commands.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoFocus, editor]);

  if (!editor) return null;

  const toolbar = editable && showToolbar ? (
    <EditorToolbar editor={editor} compact={compact} />
  ) : null;

  return (
    // The editor is content, and states it: a press surface above it (a card
    // that arms on hold) turns selection off, and Safari lets that leak into
    // an editable descendant.
    <Box className={cssClass} userSelect="text">
      {toolbar}
      <Box paddingX={contentPaddingX} paddingY={contentPaddingY}>
        <EditorContent editor={editor} />
      </Box>
      {blockHandles && editable ? (
        <DragHandle
          computePositionConfig={BLOCK_HANDLE_POSITION}
          editor={editor}
          onNodeChange={({ node, pos }) => setHandleBlock({ node, pos })}
        >
          {/* The handle's box REACHES the text: the plugin hides the handle
            * the moment the pointer leaves the editor for anything but the
            * handle itself, so the 8px between grip and text has to belong
            * to the handle, not to the gap — else the grip vanishes on the
            * way to it. The visible control sits at the box's leading edge;
            * the trailing 8px is a silent bridge. */}
          <Box paddingRight="sm">
          {slashCommands && isEmptyParagraph(handleBlock.node) ? (
            // An empty block has nothing to drag; its gutter mark adds
            // instead — the same door as typing "/", opened by a press.
            <Tooltip>
              <Box
                align="center"
                aria-label="Add block"
                as="button"
                cursor="pointer"
                height={20}
                hoverBackground
                justify="center"
                onClick={() =>
                  editor.chain().focus(handleBlock.pos + 1).insertContent("/").run()
                }
                render={<TooltipTrigger />}
                rounded="sm"
                width={16}
              >
                <Icon color="muted" name="plus" size="sm" />
              </Box>
              <TooltipPopup side="top">Add block</TooltipPopup>
            </Tooltip>
          ) : (
            <Box
              align="center"
              cursor="grab"
              height={20}
              hoverBackground
              justify="center"
              rounded="sm"
              width={16}
            >
              <Icon color="muted" name="grip-vertical" size="sm" />
            </Box>
          )}
          </Box>
        </DragHandle>
      ) : null}
    </Box>
  );
}

function isEmptyParagraph(node: ProseMirrorNode | null): boolean {
  return node?.type.name === "paragraph" && node.content.size === 0;
}

/** The block handle's seat: to the hovered block's left, top-aligned, flush
 *  against the text — the handle's own trailing padding is the 8px that puts
 *  the 16px grip on the gutter's glyph column. */
const BLOCK_HANDLE_POSITION = { placement: "left-start" as const, middleware: [offset(0)] };
